import { useEffect, useLayoutEffect, useMemo, useRef } from "react";

import {
  type ChartingLibraryWidgetOptions,
  type ChartStyle,
  type EntityId,
  type IChartingLibraryWidget,
  type IChartWidgetApi,
  type LanguageCode,
  type ResolutionString,
  type StudyEventType,
  widget,
} from "~/chart";
import type {
  ChartHistoricalOrderFill,
  ChartOpenLimitOrder,
  ResolvedTradingViewChartProps,
} from "./TradingViewChart.types";
import { createTradingViewDatafeed } from "./tradingView.datafeed";
import { toTradingViewDatafeedConfig } from "./tradingView.utils";

/**
 * Parses the `?lang=` query-string parameter from the supplied URL search
 * string. Returns `null` when the parameter is absent and an empty string
 * `""` when `?lang=` is present but has no value — matching v2 behavior
 * exactly. The call site uses `|| "en"` (not `??`) so that both `null` and
 * the empty string fall back to English.
 *
 * Accepts the search string as a parameter so tests can exercise locale
 * parsing without navigating jsdom.
 */
export const getLanguageFromURL = (search: string): LanguageCode | null => {
  const regex = /[?&]lang=([^&#]*)/;
  const results = regex.exec(search);
  return results === null
    ? null
    : (decodeURIComponent(results[1].replace(/\+/g, " ")) as LanguageCode);
};

/**
 * `ChartingLibraryWidgetOptions` types `height` and `width` as `number`.
 * The public `TradingViewChartProps` compatibility surface accepts
 * `number | string` (e.g. `"100%"` is the default width). This narrow local
 * type bridges that gap deliberately without using `any`.
 *
 * Only `height` and `width` are replaced; the library `overrides` typing is
 * kept as-is because `WidgetOverrides` carries an index signature
 * `[key: string]: string | number | boolean` that covers the runtime-valid
 * `backgrounds.preMarket/postMarket.color` keys even though they are absent
 * from the named-key portion of the type.
 *
 * `ChartingLibraryWidgetOptions` is a structural subtype of
 * `WidgetCompatOptions` (its `height?: number` is assignable to
 * `height?: number | string`), so the single narrowing cast
 * `options as ChartingLibraryWidgetOptions` is sound and avoids `as unknown
 * as`.
 */
type WidgetCompatOptions = Omit<
  ChartingLibraryWidgetOptions,
  "height" | "width"
> & {
  height?: number | string;
  width?: number | string;
};

const getStudyComparisonSymbol = (
  chart: IChartWidgetApi,
  studyId: EntityId,
): string | undefined => {
  const symbolInput = chart
    .getStudyById(studyId)
    .getInputValues()
    .find((input) => String(input.id).toLowerCase() === "symbol");
  return typeof symbolInput?.value === "string" ? symbolInput.value : undefined;
};

const getNativeComparisonSymbolsByStudyId = (
  chart: IChartWidgetApi,
): Map<EntityId, string> => {
  const symbolsByStudyId = new Map<EntityId, string>();
  for (const study of chart.getAllStudies()) {
    const symbol = getStudyComparisonSymbol(chart, study.id);
    if (symbol) {
      symbolsByStudyId.set(study.id, symbol);
    }
  }
  return symbolsByStudyId;
};

const replaceComparisonStudies = async (
  chart: IChartWidgetApi,
  comparisonSymbols: readonly string[],
  primarySymbol: string,
): Promise<void> => {
  const remainingSymbols = new Set(
    comparisonSymbols.filter((symbol) => symbol !== primarySymbol),
  );
  for (const study of chart.getAllStudies()) {
    const symbol = getStudyComparisonSymbol(chart, study.id);
    if (!symbol || !remainingSymbols.delete(symbol)) {
      chart.removeEntity(study.id);
    }
  }
  for (const symbol of remainingSymbols) {
    await chart.createStudy("Compare", false, false, { symbol });
  }
};

const isLoadableChartState = (state: Record<string, unknown>): boolean =>
  Array.isArray(state.charts) &&
  state.charts.every(
    (chart) =>
      typeof chart === "object" &&
      chart !== null &&
      Array.isArray((chart as { panes?: unknown }).panes),
  );

interface ShapeSynchronizationState<T> {
  shapesRef: { current: Map<string, EntityId> };
  itemsByShapeRef: { current: Map<EntityId, T> };
  revisionRef: { current: number };
  queueRef: { current: Promise<void> };
  readyWidgetRef: { current: IChartingLibraryWidget | null };
}

const synchronizeOpenLimitOrderShapes = (
  tvWidget: IChartingLibraryWidget,
  orders: ChartOpenLimitOrder[],
  state: ShapeSynchronizationState<ChartOpenLimitOrder>,
): void => {
  const { shapesRef, itemsByShapeRef, queueRef, readyWidgetRef, revisionRef } =
    state;
  const revision = revisionRef.current + 1;
  revisionRef.current = revision;
  const sync = async () => {
    if (
      readyWidgetRef.current !== tvWidget ||
      revision !== revisionRef.current
    ) {
      return;
    }

    const chart = tvWidget.activeChart();
    for (const shapeId of shapesRef.current.values()) {
      chart.removeEntity(shapeId, { disableUndo: true });
    }
    shapesRef.current.clear();
    itemsByShapeRef.current.clear();

    if (orders.length === 0) {
      return;
    }

    for (const order of orders) {
      const shapeId = await chart.createMultipointShape(
        [{ time: order.placedAt, price: order.limitPrice }],
        {
          shape: "horizontal_ray",
          text: `${order.side === "buy" ? "Buy" : "Sell"} Limit ${order.quantity}`,
          lock: true,
          disableSelection: false,
          disableSave: true,
          disableUndo: true,
          overrides: {
            "linetoolhorzray.linecolor":
              order.side === "buy" ? "#3B82F6" : "#22C55E",
            "linetoolhorzray.textcolor":
              order.side === "buy" ? "#3B82F6" : "#22C55E",
            "linetoolhorzray.linewidth": 2,
          },
        },
      );
      if (
        readyWidgetRef.current !== tvWidget ||
        revision !== revisionRef.current
      ) {
        chart.removeEntity(shapeId, { disableUndo: true });
        return;
      }
      chart.getShapeById(shapeId).setSelectionEnabled(true);
      chart.getShapeById(shapeId).setUserEditEnabled(false);
      shapesRef.current.set(order.id, shapeId);
      itemsByShapeRef.current.set(shapeId, order);
    }
  };

  const next = queueRef.current.then(sync);
  queueRef.current = next.catch(() => undefined);
};

const synchronizeHistoricalFillShapes = (
  tvWidget: IChartingLibraryWidget,
  fills: ChartHistoricalOrderFill[],
  state: ShapeSynchronizationState<ChartHistoricalOrderFill>,
): void => {
  const { shapesRef, itemsByShapeRef, queueRef, readyWidgetRef, revisionRef } =
    state;
  const revision = revisionRef.current + 1;
  revisionRef.current = revision;
  const sync = async () => {
    if (
      readyWidgetRef.current !== tvWidget ||
      revision !== revisionRef.current
    ) {
      return;
    }

    const chart = tvWidget.activeChart();
    for (const shapeId of shapesRef.current.values()) {
      chart.removeEntity(shapeId, { disableUndo: true });
    }
    shapesRef.current.clear();
    itemsByShapeRef.current.clear();

    for (const fill of fills) {
      const isBuy = fill.side === "buy";
      const color = isBuy ? "rgb(59, 130, 246)" : "rgb(34, 197, 94)";
      const arrowTool = isBuy ? "linetoolarrowmarkup" : "linetoolarrowmarkdown";
      const orderType = `${fill.orderType.charAt(0).toUpperCase()}${fill.orderType.slice(1)}`;
      const shapeId = await chart.createShape(
        { time: fill.filledAt, price: fill.price },
        {
          shape: isBuy ? "arrow_up" : "arrow_down",
          text: `${isBuy ? "Buy" : "Sell"} ${orderType} ${fill.quantity}`,
          lock: true,
          disableSelection: false,
          disableSave: true,
          disableUndo: true,
          overrides: {
            [`${arrowTool}.arrowColor`]: color,
            [`${arrowTool}.color`]: color,
            [`${arrowTool}.fontsize`]: 14,
          },
        },
      );
      if (
        readyWidgetRef.current !== tvWidget ||
        revision !== revisionRef.current
      ) {
        chart.removeEntity(shapeId, { disableUndo: true });
        return;
      }
      chart.getShapeById(shapeId).setProperties({
        arrowColor: color,
        color,
      });
      chart.getShapeById(shapeId).setSelectionEnabled(true);
      chart.getShapeById(shapeId).setUserEditEnabled(false);
      shapesRef.current.set(fill.id, shapeId);
      itemsByShapeRef.current.set(shapeId, fill);
    }
  };

  const next = queueRef.current.then(sync);
  queueRef.current = next.catch(() => undefined);
};

export const useTradingViewChartController = (
  resolved: ResolvedTradingViewChartProps,
): void => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify stabilizes the memo across renders with identical array contents, matching v2 behavior
  const stableEnabledFeatures = useMemo(
    () => resolved.enabledFeatures,
    [JSON.stringify(resolved.enabledFeatures)],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify stabilizes the memo across renders with identical array contents, matching v2 behavior
  const stableDisabledFeatures = useMemo(
    () => resolved.disabledFeatures,
    [JSON.stringify(resolved.disabledFeatures)],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify stabilizes the memo across renders with identical object contents, matching v2 behavior
  const stableStudiesOverrides = useMemo(
    () => resolved.studiesOverrides,
    [JSON.stringify(resolved.studiesOverrides)],
  );
  const latestThemeRef = useRef(resolved.theme);
  const latestDateRangeRef = useRef(resolved.dateRange);
  const latestIntervalRef = useRef(resolved.interval);
  const latestSymbolRef = useRef(resolved.symbol);
  const latestOnStateChangeRef = useRef(resolved.onStateChange);
  const latestOnSymbolChangeRef = useRef(resolved.onSymbolChange);
  const latestSavedStateRef = useRef(resolved.savedState);
  const latestComparisonSymbolsRef = useRef(resolved.comparisonSymbols);
  const nativeComparisonSymbolsByStudyIdRef = useRef(
    new Map<EntityId, string>(),
  );
  const latestOnComparisonSymbolsChangeRef = useRef(
    resolved.onComparisonSymbolsChange,
  );
  const latestOnContextMenuRef = useRef(resolved.onContextMenu);
  const latestOnOrderOverlaySelectRef = useRef(resolved.onOrderOverlaySelect);
  const latestOpenLimitOrdersRef = useRef(resolved.openLimitOrders);
  const latestHistoricalOrderFillsRef = useRef(resolved.historicalOrderFills);
  const openLimitOrderShapesRef = useRef(new Map<string, EntityId>());
  const openLimitOrdersByShapeRef = useRef(
    new Map<EntityId, ChartOpenLimitOrder>(),
  );
  const historicalFillsByShapeRef = useRef(
    new Map<EntityId, ChartHistoricalOrderFill>(),
  );
  const openLimitOrderShapesRevisionRef = useRef(0);
  const openLimitOrderShapesQueueRef = useRef<Promise<void>>(Promise.resolve());
  const historicalFillShapesRef = useRef(new Map<string, EntityId>());
  const historicalFillShapesRevisionRef = useRef(0);
  const historicalFillShapesQueueRef = useRef<Promise<void>>(Promise.resolve());
  const ignoreNextEmptyOrderOverlaySelectionRef = useRef(false);
  const chartDataLoadedRef = useRef(false);
  const appliedSavedStateFingerprintRef = useRef<string | null>(null);
  const savedStateLoadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const appliedIntervalRef = useRef<string | null>(null);
  const hadDateRangeRef = useRef(false);
  const widgetRef = useRef<IChartingLibraryWidget | null>(null);
  const readyWidgetRef = useRef<IChartingLibraryWidget | null>(null);
  latestThemeRef.current = resolved.theme;
  latestDateRangeRef.current = resolved.dateRange;
  latestIntervalRef.current = resolved.interval;
  latestSymbolRef.current = resolved.symbol;
  latestOnStateChangeRef.current = resolved.onStateChange;
  latestOnSymbolChangeRef.current = resolved.onSymbolChange;
  latestSavedStateRef.current = resolved.savedState;
  latestComparisonSymbolsRef.current = resolved.comparisonSymbols;
  latestOnComparisonSymbolsChangeRef.current =
    resolved.onComparisonSymbolsChange;
  latestOnContextMenuRef.current = resolved.onContextMenu;
  latestOnOrderOverlaySelectRef.current = resolved.onOrderOverlaySelect;
  latestOpenLimitOrdersRef.current = resolved.openLimitOrders;
  latestHistoricalOrderFillsRef.current = resolved.historicalOrderFills;
  const savedStateFingerprint = resolved.savedState
    ? JSON.stringify(resolved.savedState)
    : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: individual resolved fields are listed deliberately; toTradingViewDatafeedConfig receives the full object but only the listed fields should trigger a rebuild. `interval` is intentionally omitted — range-driven resolution changes apply via setResolution without remounting.
  useLayoutEffect(() => {
    let disposed = false;

    const { datafeed, dispose } = createTradingViewDatafeed(
      toTradingViewDatafeedConfig(resolved),
    );

    const options: WidgetCompatOptions = {
      symbol: resolved.symbol,
      datafeed,
      interval: resolved.interval as ResolutionString,
      container: resolved.container,
      library_path: resolved.libraryPath,
      custom_css_url: "chart-custom.css",
      enabled_features: stableEnabledFeatures,
      disabled_features: stableDisabledFeatures,
      studies_overrides: stableStudiesOverrides,
      charts_storage_url: resolved.chartsStorageUrl,
      charts_storage_api_version: resolved.chartsStorageApiVersion,
      client_id: resolved.clientId,
      user_id: resolved.userId,
      fullscreen: resolved.fullscreen,
      autosize: resolved.autosize,
      height: resolved.height,
      width: resolved.width,
      theme: resolved.theme,
      timezone: resolved.timezone,
      overrides: {
        "mainSeriesProperties.style": resolved.seriesStyle as ChartStyle,
        "backgrounds.preMarket.color": "rgba(0, 0, 0, 0.03)",
        "backgrounds.postMarket.color": "rgba(0, 0, 0, 0.03)",
      },
      locale: getLanguageFromURL(window.location.search) || "en",
    };

    const tvWidget = new widget(options as ChartingLibraryWidgetOptions);
    widgetRef.current = tvWidget;
    savedStateLoadQueueRef.current = Promise.resolve();
    appliedIntervalRef.current = resolved.interval;
    hadDateRangeRef.current = Boolean(resolved.dateRange);
    let autoSaveSubscribed = false;
    let chartApi: IChartWidgetApi | null = null;
    let syncOpenLimitOrdersAfterDataLoad: (() => void) | null = null;
    let onOrderSelectionChanged: (() => void) | null = null;
    let onChartSymbolChanged: ((symbol: { name: string }) => void) | null =
      null;
    let onNativeComparisonChanged: ((studyId: EntityId) => void) | null = null;
    let onNativeComparisonAdded: (() => void) | null = null;
    let onNativeStudyEvent:
      | ((studyId: EntityId, eventType: StudyEventType) => void)
      | null = null;
    const nativeComparisonReconcileTimers = new Map<EntityId, number>();
    let nativeComparisonDiscoveryTimer: number | null = null;
    let chartDocument: Document | null = null;
    let onChartDocumentPointerDown: (() => void) | null = null;
    const saveState = () => {
      tvWidget.save((state) => {
        appliedSavedStateFingerprintRef.current = JSON.stringify(state);
        latestOnStateChangeRef.current?.(
          structuredClone(state) as Record<string, unknown>,
        );
      });
    };

    tvWidget.onChartReady(() => {
      if (disposed) {
        return;
      }
      readyWidgetRef.current = tvWidget;
      tvWidget.subscribe("onAutoSaveNeeded", saveState);
      autoSaveSubscribed = true;

      chartApi = tvWidget.activeChart();
      const readyChartApi = chartApi;
      chartDocument =
        document.getElementById(resolved.container)?.querySelector("iframe")
          ?.contentDocument ?? null;
      onChartDocumentPointerDown = () => {
        latestOnOrderOverlaySelectRef.current?.(undefined);
      };
      chartDocument?.addEventListener(
        "pointerdown",
        onChartDocumentPointerDown,
        true,
      );
      syncOpenLimitOrdersAfterDataLoad = () => {
        chartDataLoadedRef.current = true;
        synchronizeOpenLimitOrderShapes(
          tvWidget,
          latestOpenLimitOrdersRef.current,
          {
            shapesRef: openLimitOrderShapesRef,
            itemsByShapeRef: openLimitOrdersByShapeRef,
            revisionRef: openLimitOrderShapesRevisionRef,
            queueRef: openLimitOrderShapesQueueRef,
            readyWidgetRef,
          },
        );
        synchronizeHistoricalFillShapes(
          tvWidget,
          latestHistoricalOrderFillsRef.current,
          {
            shapesRef: historicalFillShapesRef,
            itemsByShapeRef: historicalFillsByShapeRef,
            revisionRef: historicalFillShapesRevisionRef,
            queueRef: historicalFillShapesQueueRef,
            readyWidgetRef,
          },
        );
      };
      readyChartApi
        .onDataLoaded()
        .subscribe(null, syncOpenLimitOrdersAfterDataLoad);
      onOrderSelectionChanged = () => {
        const selection = readyChartApi.selection();
        const selectedShape = selection
          .allSources()
          .find(
            (id) =>
              openLimitOrdersByShapeRef.current.has(id) ||
              historicalFillsByShapeRef.current.has(id),
          );
        const overlay = selectedShape
          ? (openLimitOrdersByShapeRef.current.get(selectedShape) ??
            historicalFillsByShapeRef.current.get(selectedShape))
          : undefined;
        if (!overlay && ignoreNextEmptyOrderOverlaySelectionRef.current) {
          ignoreNextEmptyOrderOverlaySelectionRef.current = false;
          return;
        }
        latestOnOrderOverlaySelectRef.current?.(overlay);
        if (overlay) {
          ignoreNextEmptyOrderOverlaySelectionRef.current = true;
          selection.clear();
        }
      };
      readyChartApi
        .selection()
        .onChanged()
        .subscribe(null, onOrderSelectionChanged);
      onChartSymbolChanged = ({ name }) => {
        latestOnSymbolChangeRef.current?.(name);
      };
      readyChartApi.onSymbolChanged().subscribe(null, onChartSymbolChanged);
      const publishTrackedNativeComparisonSymbols = () =>
        latestOnComparisonSymbolsChangeRef.current?.([
          ...nativeComparisonSymbolsByStudyIdRef.current.values(),
        ]);
      const reconcileNativeComparisonStudy = (
        studyId: EntityId,
        attempt = 0,
      ) => {
        if (nativeComparisonReconcileTimers.has(studyId)) {
          return;
        }
        const timer = window.setTimeout(
          () => {
            nativeComparisonReconcileTimers.delete(studyId);
            let symbol: string | undefined;
            try {
              symbol = getStudyComparisonSymbol(readyChartApi, studyId);
            } catch {
              symbol = undefined;
            }
            if (symbol) {
              nativeComparisonSymbolsByStudyIdRef.current.set(studyId, symbol);
              publishTrackedNativeComparisonSymbols();
            } else if (attempt < 100) {
              reconcileNativeComparisonStudy(studyId, attempt + 1);
            }
          },
          attempt === 0 ? 0 : 50,
        );
        nativeComparisonReconcileTimers.set(studyId, timer);
      };
      const mergeDiscoveredNativeComparisonStudies = (): boolean => {
        let didDiscoverSymbol = false;
        for (const study of readyChartApi.getAllStudies()) {
          let symbol: string | undefined;
          try {
            symbol = getStudyComparisonSymbol(readyChartApi, study.id);
          } catch {
            symbol = undefined;
          }
          if (symbol) {
            const previousSymbol =
              nativeComparisonSymbolsByStudyIdRef.current.get(study.id);
            nativeComparisonSymbolsByStudyIdRef.current.set(study.id, symbol);
            didDiscoverSymbol ||= previousSymbol !== symbol;
          }
        }
        return didDiscoverSymbol;
      };
      const discoverNativeComparisonStudies = (attempt = 0) => {
        if (disposed || nativeComparisonDiscoveryTimer !== null) {
          return;
        }
        nativeComparisonDiscoveryTimer = window.setTimeout(
          () => {
            nativeComparisonDiscoveryTimer = null;
            if (disposed) {
              return;
            }
            if (mergeDiscoveredNativeComparisonStudies()) {
              publishTrackedNativeComparisonSymbols();
            }
            if (attempt < 100) {
              discoverNativeComparisonStudies(attempt + 1);
            }
          },
          attempt === 0 ? 0 : 50,
        );
      };
      onNativeComparisonChanged = (studyId) =>
        reconcileNativeComparisonStudy(studyId);
      onNativeComparisonAdded = () => discoverNativeComparisonStudies();
      onNativeStudyEvent = (studyId, eventType) => {
        if (eventType === "create") {
          // TradingView emits create before the new study can be queried.
          reconcileNativeComparisonStudy(studyId);
          return;
        }
        if (eventType === "remove") {
          const timer = nativeComparisonReconcileTimers.get(studyId);
          if (timer !== undefined) {
            window.clearTimeout(timer);
            nativeComparisonReconcileTimers.delete(studyId);
          }
          nativeComparisonSymbolsByStudyIdRef.current.delete(studyId);
          publishTrackedNativeComparisonSymbols();
          return;
        }
      };
      tvWidget.subscribe("add_compare", onNativeComparisonAdded);
      tvWidget.subscribe("study_event", onNativeStudyEvent);
      tvWidget.subscribe("study_properties_changed", onNativeComparisonChanged);

      if (latestOnContextMenuRef.current) {
        tvWidget.onContextMenu(
          (unixTime, price) =>
            latestOnContextMenuRef.current?.(unixTime, price) ?? [],
        );
      }

      const syncVisibleWindow = async () => {
        const savedState = latestSavedStateRef.current;
        if (savedState && isLoadableChartState(savedState)) {
          const load = savedStateLoadQueueRef.current.then(() =>
            tvWidget.load(structuredClone(savedState)),
          );
          savedStateLoadQueueRef.current = load.catch(() => undefined);
          try {
            await load;
            appliedSavedStateFingerprintRef.current =
              JSON.stringify(savedState);
          } catch {
            appliedSavedStateFingerprintRef.current = null;
          }
        }
        tvWidget.setSymbol(
          latestSymbolRef.current,
          latestIntervalRef.current as ResolutionString,
          () => undefined,
        );
        await replaceComparisonStudies(
          tvWidget.activeChart(),
          latestComparisonSymbolsRef.current ?? [],
          latestSymbolRef.current,
        );
        nativeComparisonSymbolsByStudyIdRef.current =
          getNativeComparisonSymbolsByStudyId(tvWidget.activeChart());
        await tvWidget.changeTheme(latestThemeRef.current, {
          disableUndo: true,
        });
        tvWidget.chart().setChartType(resolved.seriesStyle);
        const chart = tvWidget.activeChart();
        const interval = latestIntervalRef.current;
        if (appliedIntervalRef.current !== interval) {
          await chart.setResolution(interval as ResolutionString);
          appliedIntervalRef.current = interval;
        }
        const range = latestDateRangeRef.current;
        if (range) {
          hadDateRangeRef.current = true;
          await chart.setVisibleRange({
            from: range.from,
            to: range.to,
          });
        }
      };
      void syncVisibleWindow().then(() => discoverNativeComparisonStudies());
    });

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: teardown independently releases optional TradingView resources.
    return () => {
      disposed = true;
      openLimitOrderShapesRevisionRef.current += 1;
      for (const shapeId of openLimitOrderShapesRef.current.values()) {
        chartApi?.removeEntity(shapeId, { disableUndo: true });
      }
      openLimitOrderShapesRef.current.clear();
      openLimitOrdersByShapeRef.current.clear();
      historicalFillShapesRevisionRef.current += 1;
      for (const shapeId of historicalFillShapesRef.current.values()) {
        chartApi?.removeEntity(shapeId, { disableUndo: true });
      }
      historicalFillShapesRef.current.clear();
      historicalFillsByShapeRef.current.clear();
      chartDataLoadedRef.current = false;
      if (widgetRef.current === tvWidget) {
        widgetRef.current = null;
      }
      if (readyWidgetRef.current === tvWidget) {
        readyWidgetRef.current = null;
      }
      appliedIntervalRef.current = null;
      appliedSavedStateFingerprintRef.current = null;
      hadDateRangeRef.current = false;
      if (autoSaveSubscribed) {
        tvWidget.unsubscribe("onAutoSaveNeeded", saveState);
      }
      if (chartApi && syncOpenLimitOrdersAfterDataLoad) {
        chartApi
          .onDataLoaded()
          .unsubscribe(null, syncOpenLimitOrdersAfterDataLoad);
      }
      if (chartApi && onOrderSelectionChanged) {
        chartApi
          .selection()
          .onChanged()
          .unsubscribe(null, onOrderSelectionChanged);
      }
      if (chartApi && onChartSymbolChanged) {
        chartApi.onSymbolChanged().unsubscribe(null, onChartSymbolChanged);
      }
      if (onNativeComparisonChanged) {
        tvWidget.unsubscribe(
          "study_properties_changed",
          onNativeComparisonChanged,
        );
      }
      if (onNativeComparisonAdded) {
        tvWidget.unsubscribe("add_compare", onNativeComparisonAdded);
      }
      if (onNativeStudyEvent) {
        tvWidget.unsubscribe("study_event", onNativeStudyEvent);
      }
      for (const timer of nativeComparisonReconcileTimers.values()) {
        window.clearTimeout(timer);
      }
      nativeComparisonReconcileTimers.clear();
      if (nativeComparisonDiscoveryTimer !== null) {
        window.clearTimeout(nativeComparisonDiscoveryTimer);
      }
      if (chartDocument && onChartDocumentPointerDown) {
        chartDocument.removeEventListener(
          "pointerdown",
          onChartDocumentPointerDown,
          true,
        );
      }
      dispose();
      tvWidget.remove();
    };
  }, [
    resolved.container,
    resolved.libraryPath,
    resolved.chartsStorageUrl,
    resolved.chartsStorageApiVersion,
    resolved.clientId,
    resolved.userId,
    resolved.seriesStyle,
    resolved.fullscreen,
    resolved.autosize,
    resolved.height,
    resolved.width,
    resolved.baseUrl,
    resolved.crypto,
    resolved.cryptoDataApiUrl,
    resolved.utcOffset,
    resolved.timezone,
    resolved.exchange,
    resolved.isUnlimited,
    resolved.isOvernightTradable,
    stableEnabledFeatures,
    stableDisabledFeatures,
    stableStudiesOverrides,
  ]);

  useEffect(() => {
    const tvWidget = widgetRef.current;
    if (!tvWidget || readyWidgetRef.current !== tvWidget) {
      return;
    }

    chartDataLoadedRef.current = false;
    tvWidget.setSymbol(
      resolved.symbol,
      resolved.interval as ResolutionString,
      () => {
        if (readyWidgetRef.current !== tvWidget) {
          return;
        }
        chartDataLoadedRef.current = true;
        synchronizeOpenLimitOrderShapes(
          tvWidget,
          latestOpenLimitOrdersRef.current,
          {
            shapesRef: openLimitOrderShapesRef,
            itemsByShapeRef: openLimitOrdersByShapeRef,
            revisionRef: openLimitOrderShapesRevisionRef,
            queueRef: openLimitOrderShapesQueueRef,
            readyWidgetRef,
          },
        );
        synchronizeHistoricalFillShapes(
          tvWidget,
          latestHistoricalOrderFillsRef.current,
          {
            shapesRef: historicalFillShapesRef,
            itemsByShapeRef: historicalFillsByShapeRef,
            revisionRef: historicalFillShapesRevisionRef,
            queueRef: historicalFillShapesQueueRef,
            readyWidgetRef,
          },
        );
      },
    );
  }, [resolved.interval, resolved.symbol]);

  useEffect(() => {
    const tvWidget = widgetRef.current;
    if (!tvWidget || readyWidgetRef.current !== tvWidget) {
      return;
    }
    void tvWidget.changeTheme(resolved.theme, { disableUndo: true });
  }, [resolved.theme]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: comparisonSymbols triggers native Compare reconciliation; the effect reads its current ref.
  useEffect(() => {
    const tvWidget = widgetRef.current;
    if (!tvWidget || readyWidgetRef.current !== tvWidget) {
      return;
    }

    const sync = savedStateLoadQueueRef.current.then(() =>
      replaceComparisonStudies(
        tvWidget.activeChart(),
        latestComparisonSymbolsRef.current ?? [],
        resolved.symbol,
      ),
    );
    savedStateLoadQueueRef.current = sync.catch(() => undefined);
  }, [resolved.comparisonSymbols, resolved.symbol]);

  useEffect(() => {
    const tvWidget = widgetRef.current;
    if (
      !tvWidget ||
      readyWidgetRef.current !== tvWidget ||
      !chartDataLoadedRef.current
    ) {
      return;
    }

    synchronizeOpenLimitOrderShapes(tvWidget, resolved.openLimitOrders, {
      shapesRef: openLimitOrderShapesRef,
      itemsByShapeRef: openLimitOrdersByShapeRef,
      revisionRef: openLimitOrderShapesRevisionRef,
      queueRef: openLimitOrderShapesQueueRef,
      readyWidgetRef,
    });
  }, [resolved.openLimitOrders]);

  useEffect(() => {
    const tvWidget = widgetRef.current;
    if (
      !tvWidget ||
      readyWidgetRef.current !== tvWidget ||
      !chartDataLoadedRef.current
    ) {
      return;
    }

    synchronizeHistoricalFillShapes(tvWidget, resolved.historicalOrderFills, {
      shapesRef: historicalFillShapesRef,
      itemsByShapeRef: historicalFillsByShapeRef,
      revisionRef: historicalFillShapesRevisionRef,
      queueRef: historicalFillShapesQueueRef,
      readyWidgetRef,
    });
  }, [resolved.historicalOrderFills]);

  useEffect(() => {
    const tvWidget = widgetRef.current;
    const savedState = resolved.savedState;
    if (
      !tvWidget ||
      readyWidgetRef.current !== tvWidget ||
      !savedState ||
      !isLoadableChartState(savedState) ||
      savedStateFingerprint === appliedSavedStateFingerprintRef.current
    ) {
      return;
    }

    let cancelled = false;
    const loadReconciledState = async () => {
      const load = savedStateLoadQueueRef.current.then(() =>
        tvWidget.load(structuredClone(savedState)),
      );
      savedStateLoadQueueRef.current = load.catch(() => undefined);
      try {
        await load;
      } catch {
        return;
      }
      if (cancelled || readyWidgetRef.current !== tvWidget) {
        return;
      }
      appliedSavedStateFingerprintRef.current = savedStateFingerprint;
      tvWidget.setSymbol(
        resolved.symbol,
        latestIntervalRef.current as ResolutionString,
        () => undefined,
      );
      await replaceComparisonStudies(
        tvWidget.activeChart(),
        latestComparisonSymbolsRef.current ?? [],
        resolved.symbol,
      );
      await tvWidget.changeTheme(latestThemeRef.current, {
        disableUndo: true,
      });
      tvWidget.chart().setChartType(resolved.seriesStyle);
    };
    void loadReconciledState();

    return () => {
      cancelled = true;
    };
  }, [
    resolved.savedState,
    resolved.seriesStyle,
    resolved.symbol,
    savedStateFingerprint,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional primitive deps; avoid remounting on dateRange object identity. Interval changes use setResolution instead of rebuilding the widget.
  useEffect(() => {
    const tvWidget = widgetRef.current;
    if (!tvWidget || readyWidgetRef.current !== tvWidget) {
      return;
    }

    const applyVisibleWindow = async () => {
      const chart = tvWidget.activeChart();
      const interval = latestIntervalRef.current;
      if (appliedIntervalRef.current !== interval) {
        await chart.setResolution(interval as ResolutionString);
        appliedIntervalRef.current = interval;
      }

      const range = latestDateRangeRef.current;
      if (range) {
        hadDateRangeRef.current = true;
        await chart.setVisibleRange({
          from: range.from,
          to: range.to,
        });
        return;
      }

      if (hadDateRangeRef.current) {
        hadDateRangeRef.current = false;
        chart.executeActionById("timeScaleReset");
      }
    };

    void applyVisibleWindow();
  }, [resolved.interval, resolved.dateRange?.from, resolved.dateRange?.to]);
};
